import { FC, useEffect, useState } from 'react';
import { useRouter } from 'shared-routing';

import { SpaEntityConfigWizard } from '@common/components/SpaEntity/SpaEntityConfigWizard';
import {
  SpaEntityConfigWizardProps,
  SpaEntityConfigWizardReduxProps,
} from '@common/components/SpaEntity/SpaEntityConfigWizard/SpaEntityConfigWizard.types';
import { Routes } from '@common/constants';
import { parseDateToString } from '@utils/helpers';

import { useGetSpaSettings } from '@features/spa';
import { useGetSpaFacilityTimeslotDetail, useGetSpaFacilityTimeslots } from '@features/spa/hooks';
import { getDaysOfTheWeek } from '@utils/date/daysOfTheWeek';
import { FacilityFormStepEnum } from '../Facility.types';
import { FacilityFormStepsContainer } from '../FacilityFormStepsContainer';

export const FacilityConfigWizard: FC<
  SpaEntityConfigWizardProps & SpaEntityConfigWizardReduxProps
> = ({
  spaEntity,
  property,
  language,
  basketDetails,
  suggestions,
  bookingStep,
  selectedTimeSlot,
  isSpinnerVisible,
  existingGuests = [],
  signIn,
  destroyForm,
  handleBasketUpdate,
  journeyParams,
}) => {
  const router = useRouter();
  const isExtrasAvailable = spaEntity?.extras?.length;
  const [loginError, setLoginError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const maxDaysAdvance = spaSettings?.maxDaysAdvanceFacilities || Infinity;
  const [startDate, setStartDate] = useState(
    parseDateToString(journeyParams.spaDate || journeyParams.fromDate)
  );

  const { data: timeslots, isFetching: isFetchingTimeslots } = useGetSpaFacilityTimeslots(
    property.id,
    Number(spaEntity.id),
    startDate,
    language
  );

  const { data: spaFacilityWithTimeslotDetail } = useGetSpaFacilityTimeslotDetail(
    property.id,
    language,
    spaEntity.id,
    selectedTimeSlot?.startDateTime
  );

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isModalOpen) {
      setIsModalOpen(false);
      destroyForm('spaEntityConfigurationForm');
    }
  };

  useEffect(() => {
    if (bookingStep) {
      openModal();
    } else {
      closeModal();
    }
  }, [bookingStep]);

  const handleSignIn = async (data) => {
    try {
      await signIn(property.id, data);
      setIsSignInModalOpen(false);
    } catch (e) {
      setLoginError('invalidLogin');
    }
  };

  const handleSignInModalClose = () => {
    setIsSignInModalOpen(false);
  };

  const gotoEntityDetailPage = (): Promise<boolean> =>
    router.push({
      pathname: Routes.FACILITY_DETAIL,
      query: {
        id: spaEntity.id,
      },
    });

  const goToStep = (bookingStep: string): Promise<boolean> =>
    router.push({
      pathname: Routes.FACILITY_BOOKING,
      query: {
        id: spaEntity.id,
        bookingStep,
      },
    });

  const goToNextStep = async () => {
    if (bookingStep === FacilityFormStepEnum.AVAILABILITY && isExtrasAvailable) {
      goToStep(FacilityFormStepEnum.CUSTOMISE);
    }
  };

  const goToPreviousStep = () => {
    router.back();
  };

  const showConfirmation = () => {
    setIsConfirmationVisible(true);
  };

  const hideConfirmation = () => {
    setIsConfirmationVisible(false);
  };

  const finishWizardAction = async (data) => {
    await handleBasketUpdate(data);
    showConfirmation();
    await gotoEntityDetailPage();
  };

  const daysToFetch = getDaysOfTheWeek(
    selectedTimeSlot?.startDateTime || parseDateToString(journeyParams?.spaDate)
  );

  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  return (
    <SpaEntityConfigWizard
      isConfirmationVisible={isConfirmationVisible}
      basketDetails={basketDetails}
      hideConfirmation={hideConfirmation}
      suggestions={suggestions}
      isSignInModalOpen={isSignInModalOpen}
      handleSignInModalClose={handleSignInModalClose}
      spaEntity={spaFacilityWithTimeslotDetail || spaEntity}
      handleSignIn={handleSignIn}
      loginError={loginError}
      isModalOpen={isModalOpen}
      isSpinnerVisible={isSpinnerVisible || isFetchingTimeslots}
      goToPreviousStep={goToPreviousStep}
      gotoEntityDetailPage={gotoEntityDetailPage}
      SpaEntityFormSteps={FacilityFormStepsContainer}
      bookingStep={bookingStep}
      language={language}
      existingGuests={existingGuests}
      maxDaysAdvance={maxDaysAdvance}
      isExtrasAvailable={isExtrasAvailable}
      goToNextStep={goToNextStep}
      setIsSignInModalOpen={setIsSignInModalOpen}
      finishWizardAction={finishWizardAction}
      selectedTimeSlot={selectedTimeSlot}
      goToStep={goToStep}
      SpaEntityFormStepEnum={FacilityFormStepEnum}
      fetchingDates={isFetchingTimeslots ? daysToFetch : []}
      isSpaEntityFetching={isFetchingTimeslots}
      availableTimeslots={timeslots}
      setStartDate={setStartDate}
      modulesExcludingDisabled={property.modulesExcludingDisabled}
      hideArrivalTimes={hideArrivalTimes}
    />
  );
};
